// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { getUserEmail } from '../config/common';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../hooks';
import * as userDetailsAction from '../Action/User_Details/User_Details_Action';
import * as historyAction from '../Action/History/History_Action';
import * as featurePreviewsAction from '../Action/Feature_Previews/Feature_Previews_Action';
import * as updateInterestAction from '../Action/Update_Interest/Update_Interest_Action';
import SearchHistory from './SearchHistory';
import EarlyAdoptersPanel from './EarlyAdoptersPanel';

/**
 * Host for the /profile and /early-adopters pages (Main.tsx mounts it with `initialPanel`).
 *
 * It used to be the original Copilot chat, whose page shell these two pages shared. The chat is gone
 * (ACOP-152); what is left is the roles refresh on open, the Profile activity list and the Early
 * Adopters flow. Splitting the two pages out of this component is a separate, deliberate follow-up.
 */
const Copilot = ({ roles, setRoles, initialPanel }) => {
    const dispatch = useAppDispatch();
    const [hasEmail, setHasEmail] = useState(false);
    const [reqStatus, setReqStatus] = useState('');

    const [, setUserDetails] = useState({});
    const [chatRecentHistory, setChatRecentHistory] = useState([]);
    const [chatRecentHistoryAll, setChatRecentHistoryAll] = useState([]);
    const [isShowMore, setIsShowMore] = useState(false);

    const [betaFeatures, setBetaFeatures] = useState({
        isList: false,
        dataList: [],
        selIndx: null,
        isDetail: false,
        isComment: false,
        comment: ''
    });

    /** Sync with route on first paint so neither page flashes an empty shell before useEffect runs. */
    const [isShowEarlyAdopters, setIsShowEarlyAdopters] = useState(
        () => initialPanel === 'earlyAdopters',
    );
    const [isShowRecentHistory, setIsShowRecentHistory] = useState(
        () => initialPanel === 'profile',
    );

    useEffect(() => {
        const email = getUserEmail();
        setHasEmail(email && email.trim().length > 0);

        const reqData = localStorage.getItem('reqData');
        if (reqData) {
            const parsedReqData = JSON.parse(reqData);
            setReqStatus(parsedReqData.status);

            // ACOP-118: do NOT delete this. readReqStatus() (utils/reqStatus.ts) reads the same
            // key as the shared "is this account pending" signal for the rest of the tab's
            // session — nav.ts, DashboardSideNav.tsx, ReleaseNotes.tsx, and FeatureRequests.tsx
            // all depend on it staying readable after this page's own first mount. Copilot is the
            // universal post-login landing page (utils/landing.ts), so deleting it here silently
            // broke every one of those downstream checks for the rest of the session as soon as a
            // user's first navigation touched this page — which is virtually always immediately
            // after login.
        }

        if (reqStatus !== 'Requested') {
            getUserDetails();
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasEmail, reqStatus]);

    const getUserDetails = () => {
        const param = {email: getUserEmail()};
        dispatch(userDetailsAction.user_details({ data: JSON.stringify(param) })).then((response) => {
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            setUserDetails(response.data);
            if (response.data?.roles) {
                setRoles(response.data.roles);
            }
            window.dispatchEvent(new Event("resize"));
        }).catch(error => {
            toast.error(error.message);
        })
    };

    const handleHistoryClick = () => {
        let param = {email: getUserEmail(), type: 'userActivity'};
        dispatch(historyAction.history({ data: JSON.stringify(param) })).then((response) => {
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            setChatRecentHistoryAll(JSON.parse(JSON.stringify(response.data)));
            setChatRecentHistory(response.data.slice(0, 20));
            setIsShowMore(response.data.length > 20);
        }).catch(error => {
            toast.error(error.message);
        })
    }

    const getFeaturePreviews = (str) => {
        var param = {};
        param.email = getUserEmail();
        dispatch(featurePreviewsAction.feature_previews({ data: JSON.stringify(param) })).then((response) => {
            if (response.status === 1) {
                const newData = response.data && response.data.featurePreviewes ? response.data.featurePreviewes : [];

                setBetaFeatures(prevState => {
                    // If we're updating and have a selected index, try to maintain it
                    let newSelIndx = prevState.selIndx;
                    if (str === 'update' && prevState.selIndx !== null && prevState.dataList && prevState.dataList[prevState.selIndx]) {
                        // Find the same item in the new data by ID
                        const currentItemId = prevState.dataList[prevState.selIndx].Id;
                        const newIndex = newData.findIndex(item => item.Id === currentItemId);
                        if (newIndex !== -1) {
                            newSelIndx = newIndex;
                        }
                    }

                    return {
                        ...prevState,
                        dataList: newData,
                        selIndx: newSelIndx
                    };
                });
            }
            if(str === 'show'){
                setBetaFeatures(prevState => ({
                    ...prevState,
                    isList: true
                }));
                setIsShowEarlyAdopters(true);
            } else if(str === 'update') {
                // Keep the current view state when updating data
                // Don't call backFPDetailPage() to maintain the detail view
            }
        }).catch(error => {
            toast.error(error.message);
        })
    }

    useEffect(() => {
        if (initialPanel === 'profile') {
            setChatRecentHistory([]);
            setChatRecentHistoryAll([]);
            setIsShowMore(false);
            setIsShowRecentHistory(true);
        } else if (initialPanel === 'earlyAdopters') {
            getFeaturePreviews('show');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPanel]);

    const handleFeaturePrevClick = (indx) =>  {
        setBetaFeatures({...betaFeatures, selIndx: indx, isList: false, isDetail: true});
    }

    const backFPDetailPage = () => {
        setBetaFeatures((prev => ({
            ...prev, selIndx: null, isList: true, isDetail: false, isComment: false, comment: ''
        })));
    }

    const signUpForFP = (isInterested) =>  {
        // Check if we have valid data
        if (!betaFeatures.dataList || betaFeatures.selIndx === null || !betaFeatures.dataList[betaFeatures.selIndx]) {
            toast.error("Error: Invalid selection. Please try again.");
            return;
        }

        if (isInterested) {
            setBetaFeatures({...betaFeatures, isComment: true, isDetail: false});
        } else {
            // Directly call API to remove interest
            var param = {};
            param.email = getUserEmail();
            param['fpId'] = betaFeatures.dataList[betaFeatures.selIndx].Id;
            param['isRemove'] = true;
            dispatch(updateInterestAction.update_interest({ data: JSON.stringify(param) })).then((response) => {
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                // Show success message
                toast.success("Interest removed successfully!");
                // Refresh data while maintaining detail view
                getFeaturePreviews('update');
            }).catch(error => {
                toast.error(error.message);
            });
        }
    }

    const backFPCommentPage = () => {
        setBetaFeatures({...betaFeatures, isComment: false, isDetail: true, comment: ''});
    }

    const updateInterest = () =>  {
        // Check if we have valid data
        if (!betaFeatures.dataList || betaFeatures.selIndx === null || !betaFeatures.dataList[betaFeatures.selIndx]) {
            toast.error("Error: Invalid selection. Please try again.");
            return;
        }

        var param= {};
        param.email = getUserEmail();
        param['fpId'] = betaFeatures.dataList[betaFeatures.selIndx].Id;
        if (betaFeatures.isComment) {
          param['comment'] = betaFeatures.comment;
        }
        dispatch(updateInterestAction.update_interest({ data: JSON.stringify(param) })).then((response) => {
            if (response.status !== 1) {
                toast.error(response.message);
                return;
            }
            // Show success message
            toast.success("Interest updated successfully!");
            // Refresh data and go back to detail view
            getFeaturePreviews('update');
            setBetaFeatures(prevState => ({
                ...prevState,
                isComment: false,
                isDetail: true,
                comment: ''
            }));
        }).catch(error => {
            toast.error(error.message);
        })
    }

    if (isShowEarlyAdopters) {
        return (
            <div className="c2-container c2-container--scrollable">
                <EarlyAdoptersPanel
                    betaFeatures={betaFeatures}
                    onCardClick={handleFeaturePrevClick}
                    onBackToList={backFPDetailPage}
                    onInterested={() => signUpForFP(true)}
                    onWithdraw={() => signUpForFP(false)}
                    onBackFromComment={backFPCommentPage}
                    onCommentChange={(v) =>
                        setBetaFeatures((prev) => ({ ...prev, comment: v }))
                    }
                    onSaveInterest={updateInterest}
                    onCancelComment={backFPCommentPage}
                />
            </div>
        );
    }

    if (isShowRecentHistory) {
        return (
            <div className="c2-container c2-container--scrollable">
                <SearchHistory
                    isShowRecentHistory={isShowRecentHistory}
                    handleHistoryClick={handleHistoryClick}
                    setChatRecentHistory={setChatRecentHistory}
                    chatRecentHistoryAll={chatRecentHistoryAll}
                    chatRecentHistory={chatRecentHistory}
                    setIsShowMore={setIsShowMore}
                    isShowMore={isShowMore}
                    roles={roles}
                />
            </div>
        );
    }

    return null;
}

export default Copilot;
